import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { type ReactNode, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import {
  type PackagingShockInput,
  PackagingShockInputSchema,
  type PackagingShockOutput,
  asymmetryBullet,
  bothSidesSummary,
  sourceBullet,
  traceBullet,
} from "@aucash/rules/packaging-shock";
import { formatAud } from "@aucash/rules/income-tests";

import { useAuth } from "../../src/auth";
import { AppBar, Button, Card, Chip, Coin, Eyebrow } from "../../src/components/brand";
import { Field, MoneyInput, Segmented, YesNo } from "../../src/components/controls";
import { SHORT_DISCLAIMER } from "../../src/disclaimer";
import { trpc } from "../../src/trpc";
import { colors, fonts, radius, space, tabular } from "../../src/theme";

// packaging-shock — the consumer face of the income-tests add-backs: a salary +
// ONE reportable fringe benefit (an ordinary novated lease or s57A exempt-
// employer packaging), traced through the four means-tests it moves (MLS,
// Division 293, HELP/STSL, CCS/FTB ATI). We show BOTH the income-tax saving AND
// the income-test flow-on — never one without the other (the rules-layer ship-
// condition; the copy helpers render the pairing).
//
// Stateless-v1 (PLAN.md decision #1): the compute endpoint is a tRPC `query`
// with no persistence. This screen calls it inline and renders the result on the
// same screen (no save-by-id, no result route, no auth gate). Information, not
// advice (validation §3): we state the mechanism and cite the source — never
// "you should / shouldn't salary-package". This tool sits at a "should I
// package?" decision, so there is NO financial-product / novated-lease handoff
// anywhere on this personalised surface (the rules layer guards it; the UI must
// not reintroduce one).

const TEST_LABELS: Record<PackagingShockOutput["means"][number]["test"], string> = {
  mls: "Medicare levy surcharge",
  div293: "Division 293",
  help: "HELP / STSL repayment income",
  ccs: "Child Care Subsidy / Family Tax Benefit",
};

type Kind = PackagingShockInput["packagingKind"];

// The stateless compute payload — derived from the tRPC client so the screen's
// result shape can never drift from the API. Resolves to runPackagingShock's
// return via the orchestrator-stitched `calc.packagingShock.compute` query.
type ComputeResult = Awaited<
  ReturnType<typeof trpc.calc.packagingShock.compute.query>
>;

export default function PackagingShockIndex(): JSX.Element {
  const router = useRouter();
  const { status } = useAuth();

  const [grossSalary, setGrossSalary] = useState("");
  const [packagingKind, setPackagingKind] = useState<Kind>("novated_lease");
  const [benefitTaxableValue, setBenefitTaxableValue] = useState("");
  const [filingStatus, setFilingStatus] = useState<"single" | "family">("single");
  const [partnerIncome, setPartnerIncome] = useState("");
  const [holdsHospitalCover, setHoldsHospitalCover] = useState<boolean | null>(null);
  const [hasHelpDebt, setHasHelpDebt] = useState<boolean | null>(null);

  const [errors, setErrors] = useState<{ salary?: string; benefit?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<ComputeResult | null>(null);

  const input = useMemo<PackagingShockInput | null>(() => {
    const raw = {
      grossSalary: Number(grossSalary),
      packagingKind,
      benefitTaxableValue: Number(benefitTaxableValue),
      filingStatus,
      partnerIncome: filingStatus === "family" ? Number(partnerIncome || "0") : 0,
      partnerAti: filingStatus === "family" ? Number(partnerIncome || "0") : 0,
      holdsHospitalCover: holdsHospitalCover ?? false,
      hasHelpDebt: hasHelpDebt ?? false,
    };
    const parsed = PackagingShockInputSchema.safeParse(raw);
    return parsed.success ? parsed.data : null;
  }, [
    grossSalary,
    packagingKind,
    benefitTaxableValue,
    filingStatus,
    partnerIncome,
    holdsHospitalCover,
    hasHelpDebt,
  ]);

  const onSubmit = (): void => {
    const next: { salary?: string; benefit?: string } = {};
    if (grossSalary.trim() === "") {
      next.salary = "Enter the salary you'd earn without packaging.";
    }
    if (benefitTaxableValue.trim() === "") {
      next.benefit = "Enter the taxable value of the packaged benefit.";
    }
    setErrors(next);
    if (Object.keys(next).length > 0 || input === null || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    trpc.calc.packagingShock.compute
      .query({ input })
      .then((res) => setResult(res))
      .catch((err: unknown) => {
        setSubmitError(
          err instanceof Error ? err.message : "We couldn't run the calculator.",
        );
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar pill="Salary packaging" />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <View style={styles.navRow}>
            <Pressable onPress={() => router.push("/")} accessibilityRole="button">
              <Text style={styles.navLink}>← Calculators</Text>
            </Pressable>
            {status === "authed" ? (
              <Pressable onPress={() => router.push("/my-results")} accessibilityRole="button">
                <Text style={styles.navLink}>My results</Text>
              </Pressable>
            ) : status === "anon" ? (
              <Pressable onPress={() => router.push("/auth")} accessibilityRole="button">
                <Text style={styles.navLink}>Sign in</Text>
              </Pressable>
            ) : null}
          </View>

          <View style={styles.header}>
            <Coin size={56} />
            <Eyebrow>FY26 · income-test flow-on</Eyebrow>
            <Text style={styles.title}>How does salary packaging move your income tests?</Text>
            <Text style={styles.subtitle}>
              Salary packaging — like a novated car lease — lowers your tax, but the
              reported benefit is counted as income by several means tests. See both
              sides for FY26: the tax saving and the flow-on across the Medicare levy
              surcharge, Division 293, HELP/STSL and the family-assistance income test.
            </Text>
            <Text style={styles.shortDisclaimer}>{SHORT_DISCLAIMER}</Text>
          </View>

          <Field
            label="Your salary without packaging"
            help="The cash taxable income you'd earn before any benefit is sacrificed."
            error={errors.salary}
          >
            <MoneyInput
              value={grossSalary}
              onChange={setGrossSalary}
              accessibilityLabel="Your salary without packaging"
            />
          </Field>

          <Field
            label="Type of packaging"
            help="An ordinary employer novated lease counts at full value in every income test. Section 57A exempt-employer packaging (public/NFP hospitals, PBIs, ambulance) is counted at a reduced value in the family-assistance income test only."
          >
            <Segmented<Kind>
              value={packagingKind}
              onChange={setPackagingKind}
              options={[
                { value: "novated_lease", label: "Novated lease" },
                { value: "exempt_employer", label: "Exempt employer (s57A)" },
              ]}
            />
          </Field>

          <Field
            label="Taxable value of the packaged benefit"
            help="The pre-gross-up taxable value for the FBT year. A reportable fringe benefit only flows into the income tests when this is above $2,000."
            error={errors.benefit}
          >
            <MoneyInput
              value={benefitTaxableValue}
              onChange={setBenefitTaxableValue}
              accessibilityLabel="Taxable value of the packaged benefit"
            />
          </Field>

          <Field label="Are you assessed as single or as a family?">
            <Segmented<"single" | "family">
              value={filingStatus}
              onChange={setFilingStatus}
              options={[
                { value: "single", label: "Single" },
                { value: "family", label: "Family" },
              ]}
            />
          </Field>

          {filingStatus === "family" ? (
            <Field
              label="Partner's income"
              help="Combined with yours for the family Medicare levy surcharge and the family-assistance income test."
            >
              <MoneyInput
                value={partnerIncome}
                onChange={setPartnerIncome}
                accessibilityLabel="Partner's income"
              />
            </Field>
          ) : null}

          <Field label="Do you hold an eligible private hospital policy?">
            <YesNo value={holdsHospitalCover} onChange={setHoldsHospitalCover} />
          </Field>

          <Field label="Do you have a HELP / STSL study loan?">
            <YesNo value={hasHelpDebt} onChange={setHasHelpDebt} />
          </Field>

          <Button
            label={submitting ? "Calculating…" : "See both sides →"}
            onPress={onSubmit}
            disabled={submitting}
            full
            testID="packaging-shock-submit"
          />
          {submitError !== null ? (
            <Text style={styles.submitError}>{submitError}</Text>
          ) : null}

          {submitting && result === null ? (
            <View style={styles.loading}>
              <ActivityIndicator color={colors.honey} />
            </View>
          ) : null}

          {result !== null ? <ResultBody result={result} /> : null}
        </View>
      </ScrollView>
    </View>
  );
}

function ResultBody({ result }: { result: ComputeResult }): JSX.Element {
  const { output, ruleVersion, sources, sourcesVerifiedAt } = result;
  const moved = output.means.filter((m) => m.crossesThreshold).length;

  return (
    <View style={styles.resultWrap}>
      {/* §3.2: full disclaimer at the top of the result, before any number. */}
      <View style={styles.disclaimerBlock}>
        <Text style={styles.disclaimerText}>
          {sourceBullet(ruleVersion, sourcesVerifiedAt)}
        </Text>
      </View>

      <View style={styles.resHead}>
        <Eyebrow>Both sides · FY26</Eyebrow>
        <Chip
          label={moved > 0 ? `● Moves ${moved} of 4 tests` : "✓ No threshold crossed"}
          tone={moved > 0 ? "est" : "owed"}
        />
      </View>

      {/* Both-sides headline — the income-tax saving (upside) AND the income-test
          flow-on (downside), as a single sentence. Never one without the other. */}
      <Card style={{ gap: space.md }}>
        <Text style={styles.lead}>{bothSidesSummary(output)}</Text>
        <View style={styles.split}>
          <View style={styles.cell}>
            <Text style={styles.cellKey}>Tax + Medicare levy saving</Text>
            <Text style={[styles.cellVal, { color: colors.ocean }]}>
              {formatAud(output.incomeTaxSaving)}
            </Text>
          </View>
          <View style={styles.cell}>
            <Text style={styles.cellKey}>Reported fringe benefit</Text>
            <Text style={styles.cellVal}>
              {formatAud(output.with.grossedUpFringeBenefits)}
            </Text>
          </View>
        </View>
      </Card>

      {/* The four means-test traces — each WITHOUT → WITH the benefit. */}
      <Section title="What each income test does with the benefit">
        {output.means.map((m) => (
          <Card key={m.test} tone="sand" style={{ gap: space.sm }}>
            <View style={styles.traceHead}>
              <Text style={styles.traceLabel}>{TEST_LABELS[m.test]}</Text>
              {m.crossesThreshold ? (
                <Chip label="Crosses a line" tone="est" />
              ) : null}
            </View>
            <View style={styles.traceGrid}>
              <View style={styles.traceStat}>
                <Text style={styles.cellKey}>Income without</Text>
                <Text style={styles.traceStatVal}>{formatAud(m.incomeWithout)}</Text>
              </View>
              <View style={styles.traceStat}>
                <Text style={styles.cellKey}>Income with</Text>
                <Text style={styles.traceStatVal}>{formatAud(m.incomeWith)}</Text>
              </View>
            </View>
            {m.flowOnAmount !== null && m.flowOnAmount > 0 ? (
              <Text style={styles.traceFlow}>
                Extra this test applies: {formatAud(m.flowOnAmount)}
              </Text>
            ) : null}
            <Text style={styles.traceBody}>{traceBullet(m)}</Text>
          </Card>
        ))}
      </Section>

      {/* The load-bearing exempt-vs-non-exempt mechanic, in plain English. */}
      <Section title="Why the same benefit counts differently">
        <View style={styles.whyRow}>
          <View style={styles.whyIcon}>
            <Text style={styles.whyIconText}>i</Text>
          </View>
          <Text style={styles.whyText}>{asymmetryBullet(output)}</Text>
        </View>
      </Section>

      <Section title="Where these numbers come from">
        <SourceLink label="Reportable fringe benefits gross-up — ATO" url={sources.grossUp} />
        <SourceLink label="Consequences of a reportable fringe benefit — ATO" url={sources.rfbaConsequences} />
        <SourceLink label="Medicare levy surcharge thresholds — ATO" url={sources.mls} />
        <SourceLink label="Division 293 tax — ATO" url={sources.div293} />
        <SourceLink label="Adjusted taxable income — Services Australia" url={sources.ccsAti} />
        <Text style={styles.verified}>
          Rules last verified {sourcesVerifiedAt} · ruleset {ruleVersion}
        </Text>
      </Section>
    </View>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function SourceLink({ label, url }: { label: string; url: string | undefined }): JSX.Element | null {
  if (url === undefined) return null;
  return (
    <Pressable
      onPress={() => void Linking.openURL(url)}
      accessibilityRole="link"
      style={styles.sourceLink}
    >
      <Text style={styles.sourceLinkText}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 600, gap: space.xl },
  navRow: { flexDirection: "row", justifyContent: "space-between" },
  navLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },
  header: { gap: space.sm },
  title: {
    fontFamily: fonts.display,
    fontSize: 30,
    lineHeight: 34,
    letterSpacing: -1,
    color: colors.ink,
    marginTop: space.xs,
  },
  subtitle: { fontFamily: fonts.body, fontSize: 16, lineHeight: 23, color: colors.inkSoft },
  shortDisclaimer: {
    fontFamily: fonts.bodySemibold,
    fontSize: 12,
    color: colors.muted,
    marginTop: space.xs,
  },
  submitError: { fontFamily: fonts.bodyMedium, fontSize: 14, color: colors.danger, textAlign: "center" },
  loading: { paddingVertical: space.xl, alignItems: "center" },

  resultWrap: { gap: space.xl, marginTop: space.lg },
  disclaimerBlock: {
    backgroundColor: colors.sand,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.lg,
  },
  disclaimerText: { fontFamily: fonts.body, fontSize: 12, lineHeight: 18, color: colors.inkSoft },

  resHead: { flexDirection: "row", alignItems: "center", justifyContent: "space-between" },
  lead: { fontFamily: fonts.bodyMedium, fontSize: 15.5, lineHeight: 23, color: colors.inkSoft },

  split: { flexDirection: "row", gap: space.md, marginTop: space.sm },
  cell: { flex: 1, backgroundColor: colors.sand, borderRadius: radius.md, padding: space.lg },
  cellKey: {
    fontFamily: fonts.mono,
    fontSize: 10.5,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: colors.muted,
  },
  cellVal: { fontFamily: fonts.displayHeavy, fontSize: 22, color: colors.ink, marginTop: space.sm, ...tabular },

  section: { gap: space.md },
  sectionTitle: { fontFamily: fonts.display, fontSize: 20, letterSpacing: -0.4, color: colors.ink },

  traceHead: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: space.sm },
  traceLabel: { flex: 1, fontFamily: fonts.bodySemibold, fontSize: 15, color: colors.ink },
  traceGrid: { flexDirection: "row", gap: space.md },
  traceStat: { flex: 1 },
  traceStatVal: { fontFamily: fonts.displayHeavy, fontSize: 18, color: colors.ink, marginTop: space.xs, ...tabular },
  traceFlow: { fontFamily: fonts.bodySemibold, fontSize: 13.5, color: colors.honeyDeep },
  traceBody: { fontFamily: fonts.body, fontSize: 14, lineHeight: 21, color: colors.inkSoft },

  whyRow: { flexDirection: "row", gap: space.md, alignItems: "flex-start" },
  whyIcon: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: colors.honeyTint,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 1,
  },
  whyIconText: { fontFamily: fonts.displayHeavy, fontSize: 13, color: colors.honeyDeep },
  whyText: { flex: 1, fontFamily: fonts.body, fontSize: 14.5, lineHeight: 22, color: colors.inkSoft },

  sourceLink: {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.md,
    backgroundColor: colors.card,
  },
  sourceLinkText: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.oceanDeep },
  verified: { fontFamily: fonts.body, fontSize: 12, color: colors.muted },
});
